import { Injectable, signal, WritableSignal } from '@angular/core';
import { Observable } from 'rxjs';
import { OverviewReportResponse, PieChartData, SummaryPage } from '../components/report/report.model';
import { FilterRequest } from '../shared/types';
import {
  Customer_Sentiment,
  Insights,
  Interaction_Summary,
  Interaction_Summary_List,
  Interaction_Time,
} from '../components/report/report.constants';
import { CommonService } from './common.service';

export interface OverviewReportRequestData {
  filters: FilterRequest[];
  start_date: string;
  end_date: string;
  timeZone: string;
}
@Injectable({
  providedIn: 'root',
})
export class CustomerReportService {
  private readonly HIDE_INFO_MSG = 'HIDE_INFO_MSG';
  private _hideInfoMsg: WritableSignal<boolean>;

  constructor(private commonService: CommonService) {
    const hideInfoMsg = sessionStorage.getItem(this.HIDE_INFO_MSG) ?? false;
    this._hideInfoMsg = signal<boolean>(hideInfoMsg === 'true');
  }

  public get hideInfoMsg(): WritableSignal<boolean> {
    return this._hideInfoMsg;
  }

  setHideInfoMsg(status: boolean) {
    sessionStorage.setItem(this.HIDE_INFO_MSG, `${status}`);
    this._hideInfoMsg.set(status);
  }

  getOverviewReport(requestData: OverviewReportRequestData): Observable<OverviewReportResponse> {
    return this.commonService.postRequest('summary-report/get-summary-report-data', requestData);
  }

  createInteractionPages(data: OverviewReportResponse) {
    return data.conversations.conversations.map((conversation) => {
      return {
        ConversationId: conversation.ConversationId,
        ...this.mapKeys(Interaction_Summary, conversation),
        ...this.mapKeys(Interaction_Summary_List, conversation),
        ...this.mapKeys(Interaction_Time, conversation),
        ...this.mapKeys(Customer_Sentiment, conversation),
        ...this.mapKeys(Insights, conversation, 'value'),
      };
    });
  }

    private mapKeys(mappingArray: any[], conversation: any, keyType: string = 'key'): any {
    return mappingArray.reduce((acc, { label, key, value, uiKey }) => {
      const path = value || key;
      const resolvedValue = this.resolvePath(conversation, path);

      // Format phone numbers if the key is for phone number or dialed number
      if ((uiKey === 'phoneNumber' || uiKey === 'dialedNumber') && resolvedValue) {
        acc[uiKey] = this.formatSinglePhoneNumber(resolvedValue);
      } else {
        acc[uiKey] = resolvedValue;
      }

      return acc;
    }, {});
  }

  private resolvePath(object: any, path: string): any {
    return path.split('.').reduce((o, p) => (o ? o[p] : undefined), object);
  }

  createSummaryPage(data: OverviewReportResponse): SummaryPage {
    return {
      dateRange: data.dateRange,
      aeroplaneNumber: data.aeroplaneNumber,
      phoneNumber: this.formatPhoneNumbers(data.phoneNumber),
      totalConversations: data.totalConversations.toString(),
      totalPrimaryTopics: data.totalPrimaryTopics.toString(),
      totalTopics: data.totalTopics.toString(),
      totalRootCauses: data.totalRootCauses.toString(),
      listOfPrimaryTopics: data.listOfPrimaryTopics.join(', '),
      listofRootCauses: data.listofRootCauses.join(', '),
      averageInteractionTime: data.averageInteractionTime,
      averageQueueWaitTime: data.averageQueueWaitTime,
      averageHoldTime: data.averageHoldTime,
      channelDistribution: JSON.stringify(data.channelDistribution),
      resolutionDistribution: JSON.stringify(data.resolutionDistribution),
      recentConversationsOverview: data.recentConversationsOverview || [],
      summarizationOfSummaries: data.summarizationOfSummaries || '',
    };
  }

  private formatPhoneNumbers(phoneNumbers: string[] | string): string {
    if (!phoneNumbers) {
      return '';
    }

    // Handle case where phoneNumbers is a single string
    if (typeof phoneNumbers === 'string') {
      return this.formatSinglePhoneNumber(phoneNumbers);
    }

    // Handle case where phoneNumbers is an array
    if (Array.isArray(phoneNumbers)) {
      if (phoneNumbers.length === 0) {
        return '';
      }
      return phoneNumbers.map(phone => this.formatSinglePhoneNumber(phone)).join(', ');
    }

    // Fallback: convert to string and format
    return this.formatSinglePhoneNumber(String(phoneNumbers));
  }

  private formatSinglePhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) {
      return '';
    }

    // Remove any non-digit characters except + and spaces
    let cleaned = phoneNumber.replace(/[^\d+\s]/g, '');

    // If it starts with +1, format as US number
    if (cleaned.startsWith('+1')) {
      const digits = cleaned.substring(2).replace(/\D/g, '');
      if (digits.length === 10) {
        return `+1 (${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
      } else if (digits.length === 11 && digits.startsWith('1')) {
        return `+1 (${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7)}`;
      }
    }

    // If it's a 10-digit number without country code, assume US
    const digits = cleaned.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
    }

    // If it's an 11-digit number starting with 1, format as US
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 (${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7)}`;
    }

    // For other formats, return cleaned version
    return cleaned;
  }

  transformDataToPieChartData(labels: string[], percentages: { [key: string]: number }): PieChartData[] {
    const colors = ['#B6C8A9', '#FF9B71', '#F2D0A4', '#CABAC8', '#BBDEF0'];

    return labels.map((label, index) => {
      const data: PieChartData = {
        name: label,
        y: percentages[label],
      };

      if (colors[index % colors.length]) {
        data.color = colors[index % colors.length];
      }

      return data;
    });
  }

  formatRecentConversations(conversations: any[]): Array<{emoji: string, text: string}> {
    return conversations.map((conversation) => {
      const emoji = this.getSentimentEmoji(conversation.CustomerSentimentAtEnd);
      // const text = `Focused on ${conversation.PrimaryTopic} with the intent of ${conversation.Intent}${conversation.OneLineSummary ? ` - ${conversation.OneLineSummary}` : ''}`;

      const text = `${conversation.OneLineSummary}`;
      return { emoji, text };
    });
  }

  private getSentimentEmoji(sentiment: string): string {
    switch (sentiment?.toLowerCase()) {
      case 'positive':
        return 'assets/icons/emoji_emotions.svg';
      case 'negative':
        return 'assets/icons/sentiment_extremely_dissatisfied.svg';
      case 'neutral':
        return 'assets/icons/sentiment_dissatisfied.svg';
      default:
        return 'assets/icons/sentiment_dissatisfied.svg';
    }
  }
}
